import React from "react";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  AccordionDetails,
  Stack,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  KeyboardArrowRight as ChevronRightIcon,
  KeyboardArrowLeft as ChevronLeftIcon,
  BuildCircle as BuildCircleIcon,
} from "@mui/icons-material";
import {
  CustomSelect,
  TableHeaderUpArrowIcon,
  TableHeaderDownArrowIcon,
  CustomCheckbox,
  CustomChip,
  CustomTruncateText,
  CustomLoading,
  MobileTable,
  colors,
} from "@ippon/shared-components";
import { api } from "@ippon/shared-utils";
import { AREA_TYPE_LABELS, type AreaType } from "@ippon/shared-types";
import { useNavigate } from "react-router-dom";

export type RepairStatus = "未対応" | "進行中" | "完了" | "支払い済";

export type EstimateItem = {
  id: string | number;
  companyName?: string;
  status: RepairStatus;
  amount?: number | null;
  thumbnailUrl?: string;
};

export type RepairRow = {
  id: string | number;
  title: string; // 修繕名
  expenditureType?: string; // 支出区分: "capex" | "opex" | ""
  areaType: AreaType | "";
  /** Phase 2 新: 対象区分 (section_category 形式: private_area | common_area | facility | land | other) */
  targetType?: string;
  detail: string; // 例: エレベーター、部屋番号
  category?: string;
  status: RepairStatus;
  registeredAt: string | Date; // ISOでも可
  estimates?: EstimateItem[];
  // 追加: 完全なデータを保持するためのフィールド
  repair_estimates?: any[];
  repair_images?: string[];
  rawData?: any; // 元のAPIレスポンスデータ全体
};

/** 対象区分ラベル + 配色 (key は section_category 形式) */
const TARGET_TYPE_STYLE: Record<
  string,
  { label: string; bg: string; color: string }
> = {
  private_area: {
    label: AREA_TYPE_LABELS.private,
    bg: "#E3F2FD",
    color: "#1565C0",
  },
  common_area: {
    label: AREA_TYPE_LABELS.common,
    bg: "#E8F5E9",
    color: "#2E7D32",
  },
  facility: {
    label: AREA_TYPE_LABELS.facility,
    bg: "#FFF3E0",
    color: "#E65100",
  },
  land: { label: AREA_TYPE_LABELS.land, bg: "#FBE9E7", color: "#BF360C" },
  other: { label: AREA_TYPE_LABELS.other, bg: "#F5F5F5", color: "#616161" },
};

export interface RepairsTableProps {
  rows: RepairRow[];
  onSelectionChange?: (ids: Array<string | number>) => void;
  onPatchStatus?: (id: RepairRow["id"], status: RepairStatus) => Promise<void>;
  hideAreaType?: boolean; // 区分列（共用部/専有部）の非表示
  hideDetail?: boolean; // 名称列の非表示（詳細ページ用）
  pagination?: React.ReactNode; // ページネーションコンポーネント
  loading?: boolean; // ローディング状態
  propertyId?: string; // 物件ID（パンくずリスト用）
  propertyName?: string; // 物件名（パンくずリスト用）
  fromSource?: "private-area" | "common-area" | "repairs"; // 遷移元（パンくずリスト用）
  privateAreaId?: string; // 専有部ID（パンくずリスト用）
  privateAreaName?: string; // 専有部名（パンくずリスト用）
  commonAreaId?: string; // 共用部ID（パンくずリスト用）
  commonAreaName?: string; // 共用部名（パンくずリスト用）
  /**
   * 行クリック時にプレビュードロワーを開きたい場合に指定。
   * 指定時は `/repairs/:id` への navigate より優先される。
   */
  onRowPreview?: (row: RepairRow) => void;
}

// util: YYYY/MM/DD HH:mm
const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));
const formatDateTime = (input: string | Date): string => {
  const d = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  return `${y}/${m}/${day} ${hh}:${mm}`;
};

export const statusOptions = [
  { label: "未対応", value: "未対応" },
  { label: "進行中", value: "進行中" },
  { label: "完了", value: "完了" },
  { label: "支払い済", value: "支払い済" },
] as const;

/** 修繕一覧テーブル（本コンポーネント）のヘッダー・行の高さ */
const REPAIR_TABLE_ROW_HEIGHT_PX = 42;

// Unified column sizing to keep Header and Body perfectly aligned
const columnConfig = {
  checkboxWidth: 48,
  expanderWidth: 34,
  titleFlex: 3 as number,
  areaWidth: 120,
  detailFlex: 2 as number,
  categoryFlex: 3 as number,
  statusWidth: 170,
  dateWidth: 160,
};

const TOOLTIP_SLOT_PROPS = {
  popper: {
    modifiers: [{ name: "offset", options: { offset: [0, -8] } }],
  },
};

const TruncatedTextWithTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [isTruncated, setIsTruncated] = React.useState(false);
  const content = (
    <Box component="span" sx={{ display: "block", minWidth: 0, width: "100%" }}>
      <CustomTruncateText text={text} onTruncated={setIsTruncated} />
    </Box>
  );

  if (!isTruncated || !text) {
    return content;
  }

  return (
    <Tooltip title={text} placement="top" arrow slotProps={TOOLTIP_SLOT_PROPS}>
      {content}
    </Tooltip>
  );
};

const CellTooltipWrapper: React.FC<{
  text: string;
  children: React.ReactNode;
}> = ({ text, children }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = React.useState(false);

  const measure = React.useCallback(() => {
    const element = ref.current;
    if (!element || !text) {
      setShowTooltip(false);
      return;
    }

    const styles = window.getComputedStyle(element);
    const tempElement = document.createElement("span");
    tempElement.style.visibility = "hidden";
    tempElement.style.position = "absolute";
    tempElement.style.whiteSpace = "nowrap";
    tempElement.style.font = styles.font;
    tempElement.style.fontSize = styles.fontSize;
    tempElement.style.fontFamily = styles.fontFamily;
    tempElement.style.fontWeight = styles.fontWeight;
    tempElement.style.fontStyle = styles.fontStyle;
    tempElement.style.letterSpacing = styles.letterSpacing;
    tempElement.textContent = text;

    document.body.appendChild(tempElement);
    const fullTextWidth = tempElement.getBoundingClientRect().width;
    document.body.removeChild(tempElement);

    setShowTooltip(fullTextWidth > element.getBoundingClientRect().width + 1);
  }, [text]);

  React.useLayoutEffect(() => {
    measure();
  }, [measure]);

  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [measure]);

  const content = (
    <Box ref={ref} sx={{ minWidth: 0, width: "100%" }}>
      {children}
    </Box>
  );

  if (!showTooltip) {
    return content;
  }

  return (
    <Tooltip title={text} placement="top" arrow slotProps={TOOLTIP_SLOT_PROPS}>
      {content}
    </Tooltip>
  );
};

// 共有の CustomCheckbox を使用

const EstimatePill: React.FC<{
  item: EstimateItem;
  repairRow: RepairRow;
  onNavigate: (estimateId: string | number, navigationState: any) => void;
  propertyId?: string;
  propertyName?: string;
  fromSource?: "private-area" | "common-area" | "repairs";
  privateAreaId?: string;
  privateAreaName?: string;
  commonAreaId?: string;
  commonAreaName?: string;
}> = ({
  item,
  repairRow,
  onNavigate,
  propertyId,
  propertyName,
  fromSource,
  privateAreaId,
  privateAreaName,
  commonAreaId,
  commonAreaName,
}) => {
  const fullEstimate = repairRow.repair_estimates?.find(
    (e: any) => String(e.repair_estimate_id) === String(item.id),
  );

  const handleClick = () => {
    const navigationState = {
      companyName:
        item.companyName || fullEstimate?.estimate_company_name || "会社名不明",
      title: repairRow.title,
      amount: item.amount ?? fullEstimate?.estimate_amount ?? 0,
      status: item.status || fullEstimate?.status || "待機中",
      repair_image: item.thumbnailUrl || fullEstimate?.repair_image,
      detailed_evaluation: fullEstimate?.detailed_evaluation,
      anomaly_checks: fullEstimate?.anomaly_checks,
      ai_assessed: fullEstimate?.ai_assessed,
      repairId: repairRow.id,
      repairTitle: repairRow.title,
      estimateTitle: fullEstimate?.estimate_company_name || "見積書",
      propertyId: propertyId,
      propName: propertyName,
      fromSource: fromSource,
      privateAreaId: privateAreaId,
      privateAreaName: privateAreaName,
      commonAreaId: commonAreaId,
      commonAreaName: commonAreaName,
    };
    onNavigate(item.id, navigationState);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: 32,
        border: "1px solid #d9d9d9",
        borderRadius: "10px",
        backgroundColor: "#ffffff",
        pl: 1.5,
        pr: 1.5,
        minWidth: 0,
        cursor: "pointer",
        transition: "background-color 0.2s",
        "&:hover": {
          backgroundColor: "#e9f6fa",
        },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1, fontSize: 12, color: "#3e3e3e" }}>
        <CustomTruncateText text={item.companyName || "工事会社名"} />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, height: 24 }}>
        <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
          見積金額
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#3e3e3e" }}>
          {typeof item.amount === "number" &&
          !Number.isNaN(item.amount) &&
          item.amount !== 0
            ? `${item.amount.toLocaleString()}円`
            : "-"}
        </Typography>
      </Box>
    </Box>
  );
};

export const RepairsTable: React.FC<RepairsTableProps> = ({
  rows,
  onSelectionChange,
  onPatchStatus,
  hideAreaType = false,
  hideDetail = false,
  pagination,
  loading = false,
  propertyId,
  propertyName,
  fromSource,
  privateAreaId,
  privateAreaName,
  commonAreaId,
  commonAreaName,
  onRowPreview,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const useCardLayout = isMobile;
  const cardPerPage = React.useMemo(() => {
    const columnsPerRow = useCardLayout ? 2 : isLgUp ? 4 : isMdUp ? 3 : 2;
    return columnsPerRow * 2; // 2行表示
  }, [isMdUp, isLgUp, useCardLayout]);

  // Deduplicate by id to avoid transient duplicates from upstream
  const uniqueRows = React.useMemo(() => {
    const map = new Map<string | number, RepairRow>();
    for (const r of rows || []) {
      if (!map.has(r.id)) map.set(r.id, r);
    }
    return Array.from(map.values());
  }, [rows]);

  const [selected, setSelected] = React.useState<Array<string | number>>([]);
  const [expanded, setExpanded] = React.useState<
    Record<string | number, boolean>
  >({});
  const [statusMap, setStatusMap] = React.useState<
    Record<string | number, RepairStatus>
  >(() => {
    const init: Record<string | number, RepairStatus> = {};
    uniqueRows.forEach(
      (r) => (init[r.id] = (r.status || "未対応") as RepairStatus),
    );
    return init;
  });
  // Sync local status map when rows prop changes (e.g., after reload)
  React.useEffect(() => {
    const next: Record<string | number, RepairStatus> = {};
    uniqueRows.forEach((r) => {
      const safe = (r.status || "未対応") as RepairStatus;
      next[r.id] = safe;
    });
    setStatusMap(next);
  }, [uniqueRows]);

  const [pagePerRow, setPagePerRow] = React.useState<
    Record<string | number, number>
  >({});

  // Ensure current page does not exceed total pages for any row
  React.useEffect(() => {
    const updates: Record<string | number, number> = {};
    uniqueRows.forEach((row) => {
      const totalPages = Math.max(
        1,
        Math.ceil((row.estimates?.length || 0) / cardPerPage),
      );
      const currentPage = pagePerRow[row.id] || 1;
      if (currentPage > totalPages) {
        updates[row.id] = totalPages;
      }
    });
    if (Object.keys(updates).length) {
      setPagePerRow((prev) => ({ ...prev, ...updates }));
    }
  }, [uniqueRows, cardPerPage]);

  // refs and scroll state for CustomTable-style width distribution
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [tableContainerElement, setTableContainerElement] =
    React.useState<HTMLDivElement | null>(null);
  const headerRowRef = React.useRef<HTMLDivElement>(null);
  const setTableContainerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      setTableContainerElement(node);
    },
    [],
  );

  // Column reorder/resize states (desktop only)
  const reorderableBase = React.useMemo(() => {
    const base = [
      "title",
      "expenditureType",
      ...(hideAreaType ? [] : ["areaType"]),
      ...(hideDetail ? [] : ["detail"]),
      "category",
      "status",
      "registeredAt",
    ];
    return base;
  }, [hideAreaType, hideDetail]);

  const [columnOrder, setColumnOrder] =
    React.useState<string[]>(reorderableBase);
  React.useEffect(() => {
    // Sync order when visibility of areaType changes; keep existing relative order
    setColumnOrder((prev) => {
      const keep = prev.filter((f) => reorderableBase.includes(f));
      const add = reorderableBase.filter((f) => !keep.includes(f));
      return [...keep, ...add];
    });
  }, [reorderableBase]);

  const [columnWidths, setColumnWidths] = React.useState<
    Record<string, number>
  >({});
  const manuallyResizedColumns = React.useRef<Set<string>>(new Set());
  const [isResizing, setIsResizing] = React.useState(false);
  const [resizeStartX, setResizeStartX] = React.useState(0);
  const [resizeInfo, setResizeInfo] = React.useState<{
    resizeColumn: string;
    resizeColumnWidth: number;
    rightColumns: { field: string; width: number }[];
  } | null>(null);

  const [draggedColumn, setDraggedColumn] = React.useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = React.useState<string | null>(
    null,
  );

  // Sort state - default to registeredAt descending
  const [sortField, setSortField] = React.useState<string | null>(
    "registeredAt",
  );
  const [sortDirection, setSortDirection] = React.useState<
    "asc" | "desc" | null
  >("asc");

  const columnMeta: Record<
    string,
    { label: string; width?: number; flex?: number }
  > = React.useMemo(
    () => ({
      title: { label: "工事名", flex: columnConfig.titleFlex },
      expenditureType: { label: "支出区分", width: 150 },
      areaType: { label: "場所区分", width: columnConfig.areaWidth },
      detail: { label: "名称", flex: columnConfig.detailFlex },
      category: { label: "カテゴリ", flex: columnConfig.categoryFlex },
      status: { label: "ステータス", width: columnConfig.statusWidth },
      registeredAt: { label: "登録日時", width: columnConfig.dateWidth },
    }),
    [],
  );

  // CustomTable-style: distribute container width across columns
  const runDistribute = React.useCallback(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    if (containerWidth <= 0) return;

    const fixedNonColWidth =
      columnConfig.checkboxWidth + columnConfig.expanderWidth;
    const totalForColumns = Math.max(0, containerWidth - fixedNonColWidth);

    const fixedFields = columnOrder.filter((f) => columnMeta[f]?.width != null);
    const flexFields = columnOrder.filter((f) => columnMeta[f]?.width == null);

    const fixedTotal = fixedFields.reduce(
      (sum, f) => sum + (columnMeta[f]?.width ?? 0),
      0,
    );
    const remaining = Math.max(0, totalForColumns - fixedTotal);
    const totalFlex =
      flexFields.reduce((s, f) => s + (columnMeta[f]?.flex ?? 1), 0) || 1;

    const desired: Record<string, number> = {};
    columnOrder.forEach((field) => {
      const meta = columnMeta[field];
      if (!meta) return;
      if (meta.width != null) {
        desired[field] = meta.width;
      } else {
        const flex = meta.flex ?? 1;
        desired[field] = Math.max(
          50,
          Math.floor((remaining * flex) / totalFlex),
        );
      }
    });

    setColumnWidths((prev) => {
      const next: Record<string, number> = {};
      columnOrder.forEach((field) => {
        if (manuallyResizedColumns.current.has(field) && prev[field] != null) {
          next[field] = prev[field];
        } else {
          next[field] = desired[field] ?? 0;
        }
      });

      // Ensure total does not exceed container width
      const currentTotal = columnOrder.reduce(
        (sum, f) => sum + (next[f] ?? 0),
        0,
      );
      if (currentTotal > totalForColumns && totalForColumns > 0) {
        const nonFixedFields = columnOrder.filter(
          (f) => columnMeta[f]?.width == null,
        );
        const fixedSum = columnOrder
          .filter((f) => columnMeta[f]?.width != null)
          .reduce((sum, f) => sum + (next[f] ?? 0), 0);
        const availableForNonFixed = Math.max(0, totalForColumns - fixedSum);
        const nonFixedSum = nonFixedFields.reduce(
          (sum, f) => sum + (next[f] ?? 0),
          0,
        );
        if (nonFixedSum > 0) {
          const scale = availableForNonFixed / nonFixedSum;
          nonFixedFields.forEach((f) => {
            next[f] = Math.max(50, Math.floor((next[f] ?? 0) * scale));
          });
          const newNonFixedSum = nonFixedFields.reduce(
            (sum, f) => sum + (next[f] ?? 0),
            0,
          );
          const roundingDiff = availableForNonFixed - newNonFixedSum;
          if (roundingDiff !== 0 && nonFixedFields.length > 0) {
            next[nonFixedFields[0]] = Math.max(
              50,
              (next[nonFixedFields[0]] ?? 0) + roundingDiff,
            );
          }
        }
      }

      return next;
    });
  }, [columnOrder, columnMeta]);

  React.useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        runDistribute();
      });
    });

    const resizeObserver = new ResizeObserver(() => {
      runDistribute();
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [runDistribute, tableContainerElement]);

  const handleResizeMouseDown = (e: React.MouseEvent, field: string) => {
    e.preventDefault();
    const colIndex = columnOrder.indexOf(field);
    if (colIndex === columnOrder.length - 1) return;

    const defaultWidth = columnMeta[field]?.width ?? 150;
    const resizeColumnWidth = columnWidths[field] ?? defaultWidth;

    const rightColumns = columnOrder.slice(colIndex + 1).map((f) => ({
      field: f,
      width: columnWidths[f] || columnMeta[f]?.width || 150,
    }));

    setResizeInfo({ resizeColumn: field, resizeColumnWidth, rightColumns });
    setIsResizing(true);
    setResizeStartX(e.clientX);
  };

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !resizeInfo) return;

      const deltaX = e.clientX - resizeStartX;
      const newResizeColumnWidth = Math.max(
        50,
        resizeInfo.resizeColumnWidth + deltaX,
      );
      const actualDelta = newResizeColumnWidth - resizeInfo.resizeColumnWidth;

      const rightCols = resizeInfo.rightColumns;
      const updates: Record<string, number> = {
        [resizeInfo.resizeColumn]: newResizeColumnWidth,
      };

      if (rightCols.length > 0 && actualDelta !== 0) {
        let remaining = actualDelta;

        if (actualDelta > 0) {
          const currentWidths = new Map(
            rightCols.map((rc) => [rc.field, rc.width]),
          );
          const settled = new Set<string>();

          while (remaining > 0.5) {
            const active = rightCols.filter(
              (rc) =>
                !settled.has(rc.field) &&
                (currentWidths.get(rc.field) ?? 0) > 50,
            );
            if (active.length === 0) break;

            const shrinkPerCol = remaining / active.length;
            let hitMin = false;

            for (const rc of active) {
              const curWidth = currentWidths.get(rc.field) ?? 0;
              const newWidth = Math.max(
                50,
                Math.round(curWidth - shrinkPerCol),
              );
              remaining -= curWidth - newWidth;
              currentWidths.set(rc.field, newWidth);
              if (newWidth <= 50) {
                settled.add(rc.field);
                hitMin = true;
              }
            }

            if (!hitMin) break;
          }

          for (const rc of rightCols) {
            updates[rc.field] = currentWidths.get(rc.field) ?? 50;
          }
        } else {
          const expandPerCol = -actualDelta / rightCols.length;
          for (const rc of rightCols) {
            updates[rc.field] = Math.round(rc.width + expandPerCol);
          }
          remaining = 0;
        }

        if (remaining > 0) {
          updates[resizeInfo.resizeColumn] = Math.max(
            50,
            newResizeColumnWidth - remaining,
          );
        }
      }

      setColumnWidths((prev) => ({
        ...prev,
        ...updates,
      }));
    },
    [isResizing, resizeInfo, resizeStartX],
  );

  const handleMouseUp = React.useCallback(() => {
    if (resizeInfo) {
      manuallyResizedColumns.current.add(resizeInfo.resizeColumn);
      resizeInfo.rightColumns.forEach((rc) => {
        manuallyResizedColumns.current.add(rc.field);
      });
    }
    setIsResizing(false);
    setResizeInfo(null);
  }, [resizeInfo]);

  React.useEffect(() => {
    if (!isResizing) return;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleDragStart = (e: React.DragEvent, field: string) => {
    setDraggedColumn(field);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", field);
  };
  const handleDragOver = (e: React.DragEvent, field: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(field);
  };
  const handleDragLeave = () => setDragOverColumn(null);
  const handleDrop = (e: React.DragEvent, targetField: string) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetField) {
      setDraggedColumn(null);
      setDragOverColumn(null);
      return;
    }
    setColumnOrder((prev) => {
      const next = [...prev];
      const from = next.indexOf(draggedColumn);
      const to = next.indexOf(targetField);
      if (from >= 0 && to >= 0) {
        next.splice(from, 1);
        next.splice(to, 0, draggedColumn);
      }
      return next;
    });
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleHeaderSort = (field: string) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDirection("asc");
    } else {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
      }
    }
  };

  // Sort rows
  const sortedRows = React.useMemo(() => {
    if (!sortField || !sortDirection) return uniqueRows;
    return [...uniqueRows].sort((a, b) => {
      let aVal: any = a[sortField as keyof RepairRow];
      let bVal: any = b[sortField as keyof RepairRow];
      // Special handling for registeredAt (Date)
      if (sortField === "registeredAt") {
        aVal =
          typeof aVal === "string"
            ? new Date(aVal).getTime()
            : (aVal?.getTime?.() ?? 0);
        bVal =
          typeof bVal === "string"
            ? new Date(bVal).getTime()
            : (bVal?.getTime?.() ?? 0);
      }
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "number" && typeof bVal === "number")
        return aVal - bVal;
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return -1;
      if (aStr > bStr) return 1;
      return 0;
    });
  }, [uniqueRows, sortField, sortDirection]);

  const displayRows =
    sortDirection === "desc" && sortedRows !== uniqueRows
      ? [...sortedRows].reverse()
      : sortedRows;

  React.useEffect(() => {
    onSelectionChange?.(selected);
  }, [selected, onSelectionChange]);

  const allIds = displayRows.map((r) => r.id);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selected.includes(id));
  const indeterminate =
    allIds.some((id) => selected.includes(id)) && !allSelected;

  const toggleAll = () => {
    setSelected((prev) =>
      allSelected || indeterminate
        ? prev.filter((id) => !allIds.includes(id))
        : Array.from(new Set([...prev, ...allIds])),
    );
  };

  const toggleOne = (id: string | number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleExpand = (id: string | number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleStatusChange = async (
    id: string | number,
    next: RepairStatus,
  ) => {
    const previous = statusMap[id];
    setStatusMap((m) => ({ ...m, [id]: next })); // optimistic
    try {
      if (onPatchStatus) {
        await onPatchStatus(id, next);
      } else {
        await api.patch(`/repairs/${id}`, { status: next });
      }
    } catch (_e) {
      // rollback
      setStatusMap((m) => ({ ...m, [id]: previous }));
    }
  };

  const handleNavigateToRepair = (row: RepairRow) => {
    // プレビュードロワー指定時はそちらを優先 (Notion風UX)
    if (onRowPreview) {
      onRowPreview(row);
      return;
    }
    navigate(`/repairs/${row.id}`, {
      state: {
        repair: row,
        propName: propertyName,
        propertyId: propertyId,
        fromSource: fromSource,
        privateAreaId: privateAreaId,
        privateAreaName: privateAreaName,
        commonAreaId: commonAreaId,
        commonAreaName: commonAreaName,
      },
    });
  };

  const mobileRows = React.useMemo(
    () => displayRows.map((row) => ({ ...row, name: row.title })),
    [displayRows],
  );

  const mobileColumns = [
    {
      field: "areaType",
      headerName: "場所区分",
      renderCell: ({ row }: { row: RepairRow }) =>
        TARGET_TYPE_STYLE[row.targetType || ""]?.label ||
        (row.areaType
          ? AREA_TYPE_LABELS[row.areaType as AreaType] || row.areaType
          : "-"),
    },
    ...(!hideDetail
      ? [
          {
            field: "detail",
            headerName: "詳細",
            renderCell: ({ row }: { row: RepairRow }) => row.detail || "-",
          },
        ]
      : []),
    {
      field: "category",
      headerName: "カテゴリ",
      renderCell: ({ row }: { row: RepairRow }) => row.category || "-",
    },
    {
      field: "status",
      headerName: "ステータス",
      renderCell: ({ row }: { row: RepairRow }) => (
        <CustomSelect
          key={`${row.id}_${String(statusMap[row.id] || "未対応")}`}
          name={`mobile_status_${row.id}`}
          options={statusOptions as any}
          width="100%"
          XsWidth="100%"
          fontSize="14px"
          defaultValue={(statusMap[row.id] || "未対応") as RepairStatus}
          onChange={(value) =>
            void handleStatusChange(row.id, value as RepairStatus)
          }
        />
      ),
    },
    {
      field: "registeredAt",
      headerName: "登録日時",
      renderCell: ({ row }: { row: RepairRow }) =>
        formatDateTime(row.registeredAt),
    },
    {
      field: "estimates",
      headerName: "見積",
      renderCell: ({ row }: { row: RepairRow }) => {
        const estimates = row.estimates || [];
        const page = pagePerRow[row.id] || 1;
        const totalPages = Math.max(
          1,
          Math.ceil(estimates.length / cardPerPage),
        );

        return (
          <MobileRepairEstimates
            row={row}
            page={page}
            totalPages={totalPages}
            cardPerPage={cardPerPage}
            propertyId={propertyId}
            propertyName={propertyName}
            fromSource={fromSource}
            privateAreaId={privateAreaId}
            privateAreaName={privateAreaName}
            commonAreaId={commonAreaId}
            commonAreaName={commonAreaName}
            onEstimateNavigate={(estimateId, state) =>
              navigate(`/estimates/${estimateId}`, { state })
            }
            onPrevPage={() =>
              setPagePerRow((current) => ({
                ...current,
                [row.id]: Math.max(1, (current[row.id] || 1) - 1),
              }))
            }
            onNextPage={() =>
              setPagePerRow((current) => ({
                ...current,
                [row.id]: Math.min(totalPages, (current[row.id] || 1) + 1),
              }))
            }
            onPageChange={(pageNumber) =>
              setPagePerRow((current) => ({
                ...current,
                [row.id]: pageNumber,
              }))
            }
          />
        );
      },
    },
  ];

  const renderHeader = () => (
    <Box
      ref={headerRowRef}
      sx={{
        display: "flex",
        alignItems: "center",
        // ヘッダ高さは CustomTable と揃える (36px)。データ行は REPAIR_TABLE_ROW_HEIGHT_PX (=42)。
        height: "36px",
        minHeight: "36px",
        flexShrink: 0,
        boxSizing: "border-box",
        background: "#F5F5F5",
        borderRadius: "8px",
        boxShadow: "inset 0 0 0 1px rgba(224, 224, 224, 1)",
        minWidth: 0,
      }}
    >
      {/* Checkbox column */}
      <Box
        sx={{
          width: columnConfig.checkboxWidth,
          minWidth: columnConfig.checkboxWidth,
          flexShrink: 0,
          padding: "0 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            right: 0,
            top: "6px",
            bottom: "6px",
            width: "1px",
            backgroundColor: "rgba(224, 224, 224, 1)",
          },
        }}
      >
        <CustomCheckbox
          checked={allSelected}
          indeterminate={indeterminate}
          onChange={toggleAll}
          size="small"
        />
      </Box>

      {columnOrder.map((field, idx) => {
        const meta = columnMeta[field];
        if (!meta) return null;
        const currentWidth = columnWidths[field] ?? meta.width;
        const isTitle = field === "title";
        const effectiveWidth =
          isTitle && currentWidth
            ? currentWidth + columnConfig.expanderWidth
            : currentWidth;
        const isLast = idx === columnOrder.length - 1;
        return (
          <Box
            key={field}
            draggable
            onClick={() => handleHeaderSort(field)}
            onDragStart={(e) => handleDragStart(e, field)}
            onDragOver={(e) => handleDragOver(e, field)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, field)}
            sx={{
              width: effectiveWidth,
              flex: effectiveWidth
                ? field === "registeredAt"
                  ? "0 1 auto"
                  : "0 0 auto"
                : (meta.flex ?? 1),
              minWidth: 50,
              padding: isLast ? "0 30px 0 12px" : "0 16px 0 12px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              height: "100%",
              position: "relative",
              cursor: "pointer",
              opacity: draggedColumn === field ? 0.5 : 1,
              backgroundColor:
                dragOverColumn === field
                  ? "rgba(0, 0, 0, 0.05)"
                  : "transparent",
              borderLeft:
                dragOverColumn === field ? "2px solid #0B9DBD" : "none",
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: "6px",
                bottom: "6px",
                width: isLast ? 0 : "1px",
                backgroundColor: "rgba(224, 224, 224, 1)",
              },
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            <Box
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "rgba(0, 0, 0, 0.87)",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flex: 1,
                minWidth: 0,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  width: "100%",
                  textAlign: field === "title" ? "left" : undefined,
                }}
              >
                <TruncatedTextWithTooltip text={meta.label} />
              </Box>
              {sortField === field && sortDirection === "asc" && (
                <TableHeaderUpArrowIcon />
              )}
              {sortField === field && sortDirection === "desc" && (
                <TableHeaderDownArrowIcon />
              )}
            </Box>
            {/* Resize handle - not shown on last column */}
            {!isLast && (
              <Box
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleResizeMouseDown(e, field);
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  position: "absolute",
                  right: "-14px",
                  top: 0,
                  bottom: 0,
                  width: "28px",
                  cursor: "col-resize",
                  backgroundColor: "transparent",
                  zIndex: 1,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: "50%",
                    top: 0,
                    bottom: 0,
                    width: "2px",
                    transform: "translateX(-50%)",
                    backgroundColor: "transparent",
                  },
                  "&:hover::after": {
                    backgroundColor: "rgba(0, 0, 0, 0.15)",
                  },
                  "&:active::after": {
                    backgroundColor: "rgba(0, 0, 0, 0.3)",
                  },
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );

  // Mobile Card View
  if (useCardLayout) {
    return (
      <Box ref={setTableContainerRef} sx={{ width: "100%", pb: 2 }}>
        <MobileTable
          rows={mobileRows}
          columns={mobileColumns}
          selectedIds={selected}
          onRowSelection={setSelected}
          isLoading={loading}
          hidePagination
          noDataMessage="工事が登録されていません"
          noDataIcon={
            <BuildCircleIcon
              sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
            />
          }
          handlePropertyClick={(row) => handleNavigateToRepair(row)}
        />
      </Box>
    );
  }

  // Desktop Table View
  return (
    <>
      <Box
        ref={setTableContainerRef}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflow: "visible",
          background: "#fff",
          borderRadius: 2,
        }}
      >
        {renderHeader()}
        <Box>
          {/* rows */}
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "240px",
                width: "100%",
              }}
            >
              <CustomLoading loading={true} fullScreen={false} size="medium" />
            </Box>
          ) : displayRows.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "240px",
                width: "100%",
                textAlign: "center",
              }}
            >
              <BuildCircleIcon
                sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
              />
              <Typography
                sx={{ fontSize: "14px", color: colors.text.secondary }}
              >
                工事が登録されていません
              </Typography>
            </Box>
          ) : (
            displayRows.map((row, index) => {
              const isExpanded = !!expanded[row.id];
              const estimates = row.estimates || [];
              const page = pagePerRow[row.id] || 1;
              const totalPages = Math.max(
                1,
                Math.ceil(estimates.length / cardPerPage),
              );
              // ページごとに分割
              const pages: EstimateItem[][] = [];
              for (let i = 0; i < estimates.length; i += cardPerPage) {
                pages.push(estimates.slice(i, i + cardPerPage));
              }
              const isLastRow = index === displayRows.length - 1;

              return (
                <React.Fragment key={row.id}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      height: REPAIR_TABLE_ROW_HEIGHT_PX,
                      minHeight: REPAIR_TABLE_ROW_HEIGHT_PX,
                      boxSizing: "border-box",
                      minWidth: 0,
                      backgroundColor: selected.includes(row.id)
                        ? "#f0f8ff"
                        : "transparent",
                      "&:hover": { backgroundColor: "transparent" },
                      ...(isExpanded
                        ? {}
                        : !isLastRow || !pagination
                          ? { borderBottom: "1px solid rgba(224, 224, 224, 1)" }
                          : {}),
                    }}
                  >
                    <Box
                      sx={{
                        width: columnConfig.checkboxWidth,
                        minWidth: columnConfig.checkboxWidth,
                        flexShrink: 0,
                        padding: "0 8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CustomCheckbox
                        checked={selected.includes(row.id)}
                        onChange={() => toggleOne(row.id)}
                        size="small"
                      />
                    </Box>
                    <Box
                      sx={{
                        width: columnConfig.expanderWidth,
                        minWidth: columnConfig.expanderWidth,
                        flexShrink: 0,
                        boxSizing: "border-box",
                        pl: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <IconButton
                        onClick={() => toggleExpand(row.id)}
                        size="small"
                        sx={{
                          transform: isExpanded
                            ? "rotate(90deg)"
                            : "rotate(0deg)",
                          transition: "all 0.2s",
                          color: isExpanded ? "#0E9DBC" : "#3e3e3e",
                          backgroundColor: isExpanded
                            ? "#DBF8FF"
                            : "transparent",
                          width: 26,
                          height: 26,
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor: "#DBF8FF",
                            "& svg": {
                              color: "#0E9DBC",
                            },
                          },
                        }}
                      >
                        <ChevronRightIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                    {columnOrder.map((field) => {
                      const meta = columnMeta[field];
                      if (!meta) return null;
                      const width = columnWidths[field] ?? meta.width;
                      if (field === "title") {
                        return (
                          <BodyCell
                            key={field}
                            flex={width ? undefined : meta.flex}
                            width={width}
                            ellipsis
                            tooltipText={row.title || ""}
                            padding="6px 16px 6px 8px"
                          >
                            <Box
                              onClick={() => handleNavigateToRepair(row)}
                              sx={{
                                fontSize: 14,
                                cursor: "pointer",
                                textDecoration: "underline",
                                width: "100%",
                                "&:hover": { color: "#0B9DBD" },
                              }}
                            >
                              <CustomTruncateText text={row.title || ""} />
                            </Box>
                          </BodyCell>
                        );
                      }
                      if (field === "expenditureType") {
                        const expLabel =
                          row.expenditureType === "capex"
                            ? "CAPEX（資本的支出）"
                            : row.expenditureType === "opex"
                              ? "OPEX（工事費）"
                              : "-";
                        return (
                          <BodyCell
                            key={field}
                            width={width}
                            flex={width ? undefined : meta.flex}
                            title={expLabel}
                            ellipsis
                          />
                        );
                      }
                      if (field === "areaType") {
                        const style =
                          TARGET_TYPE_STYLE[row.targetType || ""] || null;
                        const areaLabel = row.areaType
                          ? AREA_TYPE_LABELS[row.areaType as AreaType] ||
                            row.areaType
                          : "";
                        return (
                          <BodyCell
                            key={field}
                            width={width}
                            flex={width ? undefined : meta.flex}
                            title={style?.label || areaLabel}
                          >
                            <Box
                              data-testid={`repair-target-type-${row.id}`}
                              data-target-type={row.targetType || "legacy"}
                            >
                              {style ? (
                                <CustomChip
                                  label={style.label}
                                  backgroundColor={style.bg}
                                  color={style.color}
                                  fontSize="12px"
                                  fontWeight={500}
                                  height={22}
                                />
                              ) : areaLabel ? (
                                <CustomChip
                                  label={areaLabel}
                                  backgroundColor="#F5F5F5"
                                  color="#666"
                                  fontSize="12px"
                                  fontWeight={500}
                                  height={22}
                                />
                              ) : (
                                "-"
                              )}
                            </Box>
                          </BodyCell>
                        );
                      }
                      if (field === "detail") {
                        return (
                          <BodyCell
                            key={field}
                            width={width}
                            flex={width ? undefined : meta.flex}
                            title={row.detail}
                            ellipsis
                          />
                        );
                      }
                      if (field === "category") {
                        return (
                          <BodyCell
                            key={field}
                            width={width}
                            flex={width ? undefined : meta.flex}
                            title={row.category || "-"}
                            ellipsis
                          />
                        );
                      }
                      if (field === "status") {
                        return (
                          <BodyCell
                            key={field}
                            width={width}
                            flex={width ? undefined : meta.flex}
                          >
                            <CustomSelect
                              key={`${row.id}_${String(statusMap[row.id] || "未対応")}`}
                              name={`status_${row.id}`}
                              options={statusOptions as any}
                              width="140px"
                              XsWidth="140px"
                              fontSize="14px"
                              defaultValue={
                                (statusMap[row.id] || "未対応") as RepairStatus
                              }
                              onChange={(v) =>
                                handleStatusChange(row.id, v as RepairStatus)
                              }
                            />
                          </BodyCell>
                        );
                      }
                      if (field === "registeredAt") {
                        return (
                          <BodyCell
                            key={field}
                            width={width}
                            flex={width ? undefined : meta.flex}
                            allowShrink
                            title={formatDateTime(row.registeredAt)}
                            ellipsis
                          />
                        );
                      }
                      return null;
                    })}
                  </Box>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <AccordionDetails
                      sx={{
                        pt: 0,
                        pb: 1.5,
                        borderRadius: "0 !important",
                        backgroundColor: "#fff",
                        ...(isExpanded && (!isLastRow || !pagination)
                          ? { borderBottom: "1px solid rgba(224, 224, 224, 1)" }
                          : {}),
                      }}
                    >
                      <Box
                        px={1.5}
                        py={1.5}
                        sx={{
                          backgroundColor: "#f9fafb",
                          borderRadius: "8px",
                          mb: 1,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <svg
                            width="13"
                            height="16"
                            viewBox="0 0 13 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M2.96144 8.14428H9.28856V7.26928H2.96144V8.14428ZM2.96144 10.5674H9.28856V9.69237H2.96144V10.5674ZM2.96144 12.9905H6.66356V12.1155H2.96144V12.9905ZM1.41356 15.75C1.01077 15.75 0.674479 15.6151 0.404687 15.3453C0.134896 15.0755 0 14.7392 0 14.3364V1.41356C0 1.01077 0.134896 0.674479 0.404687 0.404687C0.674479 0.134896 1.01077 0 1.41356 0H8.3125L12.25 3.9375V14.3364C12.25 14.7392 12.1151 15.0755 11.8453 15.3453C11.5755 15.6151 11.2392 15.75 10.8364 15.75H1.41356ZM7.875 4.375V0.875H1.41356C1.27881 0.875 1.15536 0.931073 1.04322 1.04322C0.931073 1.15536 0.875 1.27881 0.875 1.41356V14.3364C0.875 14.4712 0.931073 14.5946 1.04322 14.7068C1.15536 14.8189 1.27881 14.875 1.41356 14.875H10.8364C10.9712 14.875 11.0946 14.8189 11.2068 14.7068C11.3189 14.5946 11.375 14.4712 11.375 14.3364V4.375H7.875Z"
                              fill="currentColor"
                            />
                          </svg>
                          <Typography
                            fontWeight={700}
                            fontSize={12}
                            color="#3e3e3e"
                          >
                            見積
                          </Typography>
                          <Typography fontSize={12} color="#6b7280">
                            {estimates.length}件
                          </Typography>
                        </Stack>
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{ position: "relative", overflow: "hidden" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                transform: `translateX(calc(-${(page - 1) * 100}% + ${(page - 1) * 8}px))`,
                                transition:
                                  "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
                              }}
                            >
                              {pages.map((pageItems, pageIdx) => (
                                <Box
                                  key={`estimate-page-${row.id}-${pageIdx}`}
                                  sx={{
                                    flex: "0 0 calc(100% - 8px)",
                                    mr: "8px",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: {
                                        xs: "repeat(2, 1fr)",
                                        md: "repeat(3, 1fr)",
                                        lg: "repeat(4, 1fr)",
                                      },
                                      gap: 0.75,
                                    }}
                                  >
                                    {pageItems.map((it) => (
                                      <EstimatePill
                                        key={it.id}
                                        item={it}
                                        repairRow={row}
                                        onNavigate={(estimateId, state) =>
                                          navigate(`/estimates/${estimateId}`, {
                                            state,
                                          })
                                        }
                                        propertyId={propertyId}
                                        propertyName={propertyName}
                                        fromSource={fromSource}
                                        privateAreaId={privateAreaId}
                                        privateAreaName={privateAreaName}
                                        commonAreaId={commonAreaId}
                                        commonAreaName={commonAreaName}
                                      />
                                    ))}
                                    {pageItems.length === 0 && (
                                      <Typography
                                        sx={{ fontSize: 12, color: "#989898" }}
                                      >
                                        見積はまだ登録されていません
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                            {totalPages > 1 && (
                              <Stack
                                direction="row"
                                spacing={1}
                                mt={1}
                                justifyContent="center"
                                alignItems="center"
                              >
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPagePerRow((m) => ({
                                      ...m,
                                      [row.id]: Math.max(
                                        1,
                                        (m[row.id] || 1) - 1,
                                      ),
                                    }));
                                  }}
                                  disabled={page === 1}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "6px",
                                    backgroundColor: "#f5f5f5",
                                    padding: "6px",
                                    "&:hover": { backgroundColor: "#e5e5e5" },
                                    "&:disabled": {
                                      backgroundColor: "#f5f5f5",
                                      opacity: 0.5,
                                    },
                                  }}
                                >
                                  <ChevronLeftIcon
                                    sx={{
                                      fontSize: 14,
                                      color: "rgba(0,0,0,0.88)",
                                    }}
                                  />
                                </IconButton>
                                {(() => {
                                  const maxDots = 5;
                                  const windowSize = Math.min(
                                    maxDots,
                                    totalPages,
                                  );
                                  let start = Math.max(
                                    1,
                                    page - Math.floor(windowSize / 2),
                                  );
                                  let end = start + windowSize - 1;
                                  if (end > totalPages) {
                                    end = totalPages;
                                    start = Math.max(1, end - windowSize + 1);
                                  }
                                  const pagesToShow = Array.from(
                                    { length: end - start + 1 },
                                    (_, i) => start + i,
                                  );
                                  return (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      sx={{
                                        minWidth: 62,
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {pagesToShow.map((pageNum) => (
                                        <Box
                                          key={`dot-${row.id}-${pageNum}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPagePerRow((m) => ({
                                              ...m,
                                              [row.id]: pageNum,
                                            }));
                                          }}
                                          sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: "50%",
                                            backgroundColor:
                                              pageNum === page
                                                ? "#6b7280"
                                                : "#e5e7eb",
                                            cursor: "pointer",
                                          }}
                                        />
                                      ))}
                                    </Stack>
                                  );
                                })()}
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPagePerRow((m) => ({
                                      ...m,
                                      [row.id]: Math.min(
                                        totalPages,
                                        (m[row.id] || 1) + 1,
                                      ),
                                    }));
                                  }}
                                  disabled={page === totalPages}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "6px",
                                    backgroundColor: "#f5f5f5",
                                    padding: "6px",
                                    "&:hover": { backgroundColor: "#e5e5e5" },
                                    "&:disabled": {
                                      backgroundColor: "#f5f5f5",
                                      opacity: 0.5,
                                    },
                                  }}
                                >
                                  <ChevronRightIcon
                                    sx={{
                                      fontSize: 14,
                                      color: "rgba(0,0,0,0.88)",
                                    }}
                                  />
                                </IconButton>
                              </Stack>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Collapse>
                </React.Fragment>
              );
            })
          )}
        </Box>
      </Box>
      {pagination && displayRows.length > 0 && !loading && (
        <Box sx={{ pt: 2, px: 2, borderTop: "1px solid #d9d9d9" }}>
          {pagination}
        </Box>
      )}
    </>
  );
};

const BodyCell: React.FC<{
  width?: number;
  flex?: number;
  children?: React.ReactNode;
  title?: string;
  tooltipText?: string;
  ellipsis?: boolean;
  padding?: string;
  allowShrink?: boolean;
}> = ({
  width,
  flex,
  children,
  title,
  tooltipText,
  ellipsis,
  padding,
  allowShrink = false,
}) => (
  <Box
    sx={{
      width,
      flex: width ? (allowShrink ? "0 1 auto" : "0 0 auto") : (flex ?? 1),
      minWidth: 50,
      height: REPAIR_TABLE_ROW_HEIGHT_PX,
      minHeight: REPAIR_TABLE_ROW_HEIGHT_PX,
      padding: padding ?? "6px 16px 6px 12px",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
    }}
  >
    {children ? (
      ellipsis && (tooltipText || title) ? (
        <CellTooltipWrapper text={tooltipText || title || ""}>
          {children}
        </CellTooltipWrapper>
      ) : (
        children
      )
    ) : ellipsis ? (
      <Box sx={{ fontSize: "14px", color: "#3e3e3e", width: "100%" }}>
        <TruncatedTextWithTooltip text={title || ""} />
      </Box>
    ) : (
      <Typography
        sx={{
          fontSize: "14px",
          color: "#3e3e3e",
        }}
      >
        {title}
      </Typography>
    )}
  </Box>
);

interface MobileRepairEstimatesProps {
  row: RepairRow;
  page: number;
  totalPages: number;
  cardPerPage: number;
  propertyId?: string;
  propertyName?: string;
  fromSource?: "private-area" | "common-area" | "repairs";
  privateAreaId?: string;
  privateAreaName?: string;
  commonAreaId?: string;
  commonAreaName?: string;
  onEstimateNavigate: (
    estimateId: string | number,
    navigationState: any,
  ) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  onPageChange: (pageNum: number) => void;
}

const MobileRepairEstimates: React.FC<MobileRepairEstimatesProps> = ({
  row,
  page,
  totalPages,
  cardPerPage,
  propertyId,
  propertyName,
  fromSource,
  privateAreaId,
  privateAreaName,
  commonAreaId,
  commonAreaName,
  onEstimateNavigate,
  onPrevPage,
  onNextPage,
  onPageChange,
}) => {
  const estimates = row.estimates || [];
  const pageItems = estimates.slice(
    (page - 1) * cardPerPage,
    page * cardPerPage,
  );

  return (
    <Box
      sx={{
        mt: 1,
        mb: 1.5,
        p: 1.5,
        backgroundColor: colors.background.light,
        borderRadius: 1,
      }}
    >
      <Typography fontSize={12} color={colors.text.secondary} sx={{ mb: 1 }}>
        {estimates.length}件
      </Typography>

      <Stack spacing={1}>
        {pageItems.map((item) => (
          <EstimatePill
            key={item.id}
            item={item}
            repairRow={row}
            onNavigate={onEstimateNavigate}
            propertyId={propertyId}
            propertyName={propertyName}
            fromSource={fromSource}
            privateAreaId={privateAreaId}
            privateAreaName={privateAreaName}
            commonAreaId={commonAreaId}
            commonAreaName={commonAreaName}
          />
        ))}
        {pageItems.length === 0 && (
          <Typography sx={{ fontSize: 12, color: colors.text.secondary }}>
            見積はまだ登録されていません
          </Typography>
        )}
      </Stack>

      {totalPages > 1 && (
        <Stack
          direction="row"
          spacing={1}
          mt={1}
          justifyContent="center"
          alignItems="center"
        >
          <IconButton
            aria-label="前の見積ページ"
            onClick={onPrevPage}
            disabled={page === 1}
            size="small"
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Stack direction="row" spacing={1} alignItems="center">
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              (pageNumber) => (
                <Box
                  key={pageNumber}
                  component="button"
                  type="button"
                  aria-label={`見積 ${pageNumber}ページ目`}
                  onClick={() => onPageChange(pageNumber)}
                  sx={{
                    width: 6,
                    height: 6,
                    border: 0,
                    p: 0,
                    borderRadius: "50%",
                    backgroundColor:
                      pageNumber === page
                        ? colors.text.secondary
                        : colors.border.light,
                    cursor: "pointer",
                  }}
                />
              ),
            )}
          </Stack>
          <IconButton
            aria-label="次の見積ページ"
            onClick={onNextPage}
            disabled={page === totalPages}
            size="small"
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
};

// Legacy mobile card component kept for the desktop row's expanded estimate layout.
export const RepairCard: React.FC<{
  row: RepairRow;
  selected: boolean;
  expanded: boolean;
  statusMap: Record<string | number, RepairStatus>;
  page: number;
  totalPages: number;
  cardPerPage: number;
  hideDetail?: boolean;
  propertyId?: string;
  propertyName?: string;
  fromSource?: "private-area" | "common-area" | "repairs";
  privateAreaId?: string;
  privateAreaName?: string;
  commonAreaId?: string;
  commonAreaName?: string;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
  onNavigate: () => void;
  onEstimateNavigate: (
    estimateId: string | number,
    navigationState: any,
  ) => void;
  onStatusChange: (status: RepairStatus) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  onPageChange: (pageNum: number) => void;
}> = ({
  row,
  selected,
  expanded,
  statusMap,
  page,
  totalPages,
  cardPerPage,
  hideDetail = false,
  propertyId,
  propertyName,
  fromSource,
  privateAreaId,
  privateAreaName,
  commonAreaId,
  commonAreaName,
  onToggleSelect,
  onToggleExpand,
  onNavigate,
  onEstimateNavigate,
  onStatusChange,
  onPrevPage,
  onNextPage,
  onPageChange,
}) => {
  const estimates = row.estimates || [];

  // ページごとに分割
  const pages: EstimateItem[][] = [];
  for (let i = 0; i < estimates.length; i += cardPerPage) {
    pages.push(estimates.slice(i, i + cardPerPage));
  }

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        backgroundColor: selected ? "#f0f8ff" : "#ffffff",
        mb: 2,
        overflow: "hidden",
      }}
    >
      {/* Card Header: 上下幅・文字・アイコンを MobileTable のヘッダーに合わせる
          (上下 8px / タイトル 14px weight500 #3e3e3e / 展開アイコンを小さく) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}
        >
          <CustomCheckbox
            checked={selected}
            onChange={onToggleSelect}
            size="small"
          />
          <Box
            onClick={onNavigate}
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: "#3e3e3e",
              cursor: "pointer",
              textDecoration: "underline",
              width: "100%",
              "&:hover": { color: "#0B9DBD" },
            }}
          >
            <CustomTruncateText text={row.title || ""} />
          </Box>
        </Box>
        <IconButton
          onClick={onToggleExpand}
          size="small"
          sx={{
            transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
            transition: "all 0.2s",
            color: expanded ? "#0E9DBC" : "#3e3e3e",
            backgroundColor: expanded ? "#DBF8FF" : "transparent",
            width: 26,
            height: 26,
            flexShrink: 0,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "#DBF8FF",
              "& svg": {
                color: "#0E9DBC",
              },
            },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Card Body */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              sx={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}
            >
              場所区分
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#3e3e3e" }}>
              {TARGET_TYPE_STYLE[row.targetType || ""]?.label ||
                (row.areaType
                  ? AREA_TYPE_LABELS[row.areaType as AreaType] || row.areaType
                  : "-")}
            </Typography>
          </Box>
          {!hideDetail && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                sx={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}
              >
                詳細
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#3e3e3e",
                  maxWidth: "60%",
                  textAlign: "right",
                }}
              >
                {row.detail}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              sx={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}
            >
              カテゴリ
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: "#3e3e3e",
                maxWidth: "60%",
                textAlign: "right",
              }}
            >
              {row.category || "-"}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}
            >
              ステータス
            </Typography>
            <Box sx={{ width: "60%" }}>
              <CustomSelect
                key={`${row.id}_${String(statusMap[row.id] || "未対応")}`}
                name={`status_${row.id}`}
                options={statusOptions as any}
                width="100%"
                XsWidth="100%"
                fontSize="14px"
                defaultValue={(statusMap[row.id] || "未対応") as RepairStatus}
                onChange={(v) => onStatusChange(v as RepairStatus)}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              sx={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}
            >
              登録日時
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#3e3e3e" }}>
              {formatDateTime(row.registeredAt)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Expanded Estimates */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <AccordionDetails
          sx={{
            pt: 0,
            pb: 1.5,
            borderRadius: "0 !important",
            backgroundColor: "#fff",
            ...(expanded
              ? { borderBottom: "1px solid rgba(224, 224, 224, 1)" }
              : {}),
          }}
        >
          <Box
            px={1.5}
            py={1.5}
            sx={{
              backgroundColor: "#f9fafb",
              borderRadius: "8px",
              mb: 1,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <svg
                width="13"
                height="16"
                viewBox="0 0 13 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.96144 8.14428H9.28856V7.26928H2.96144V8.14428ZM2.96144 10.5674H9.28856V9.69237H2.96144V10.5674ZM2.96144 12.9905H6.66356V12.1155H2.96144V12.9905ZM1.41356 15.75C1.01077 15.75 0.674479 15.6151 0.404687 15.3453C0.134896 15.0755 0 14.7392 0 14.3364V1.41356C0 1.01077 0.134896 0.674479 0.404687 0.404687C0.674479 0.134896 1.01077 0 1.41356 0H8.3125L12.25 3.9375V14.3364C12.25 14.7392 12.1151 15.0755 11.8453 15.3453C11.5755 15.6151 11.2392 15.75 10.8364 15.75H1.41356ZM7.875 4.375V0.875H1.41356C1.27881 0.875 1.15536 0.931073 1.04322 1.04322C0.931073 1.15536 0.875 1.27881 0.875 1.41356V14.3364C0.875 14.4712 0.931073 14.5946 1.04322 14.7068C1.15536 14.8189 1.27881 14.875 1.41356 14.875H10.8364C10.9712 14.875 11.0946 14.8189 11.2068 14.7068C11.3189 14.5946 11.375 14.4712 11.375 14.3364V4.375H7.875Z"
                  fill="currentColor"
                />
              </svg>
              <Typography fontWeight={700} fontSize={12} color="#3e3e3e">
                見積
              </Typography>
              <Typography fontSize={12} color="#6b7280">
                {estimates.length}件
              </Typography>
            </Stack>
            <Box sx={{ mt: 1 }}>
              <Box sx={{ position: "relative", overflow: "hidden" }}>
                <Box
                  sx={{
                    display: "flex",
                    transform: `translateX(calc(-${(page - 1) * 100}% + ${(page - 1) * 8}px))`,
                    transition:
                      "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
                  }}
                >
                  {pages.map((pageItems, pageIdx) => (
                    <Box
                      key={`mobile-estimate-page-${row.id}-${pageIdx}`}
                      sx={{
                        flex: "0 0 calc(100% - 8px)",
                        mr: "8px",
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {pageItems.map((it) => (
                        <EstimatePill
                          key={it.id}
                          item={it}
                          repairRow={row}
                          onNavigate={onEstimateNavigate}
                          propertyId={propertyId}
                          propertyName={propertyName}
                          fromSource={fromSource}
                          privateAreaId={privateAreaId}
                          privateAreaName={privateAreaName}
                          commonAreaId={commonAreaId}
                          commonAreaName={commonAreaName}
                        />
                      ))}
                      {pageItems.length === 0 && (
                        <Typography sx={{ fontSize: 12, color: "#989898" }}>
                          見積はまだ登録されていません
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
              {totalPages > 1 && (
                <Stack
                  direction="row"
                  spacing={1}
                  mt={1}
                  justifyContent="center"
                  alignItems="center"
                >
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onPrevPage();
                    }}
                    disabled={page === 1}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "6px",
                      backgroundColor: "#f5f5f5",
                      padding: "6px",
                      "&:hover": { backgroundColor: "#e5e5e5" },
                      "&:disabled": {
                        backgroundColor: "#f5f5f5",
                        opacity: 0.5,
                      },
                    }}
                  >
                    <ChevronLeftIcon
                      sx={{
                        fontSize: 14,
                        color: "rgba(0,0,0,0.88)",
                      }}
                    />
                  </IconButton>
                  {(() => {
                    const maxDots = 5;
                    const windowSize = Math.min(maxDots, totalPages);
                    let start = Math.max(1, page - Math.floor(windowSize / 2));
                    let end = start + windowSize - 1;
                    if (end > totalPages) {
                      end = totalPages;
                      start = Math.max(1, end - windowSize + 1);
                    }
                    const pagesToShow = Array.from(
                      { length: end - start + 1 },
                      (_, i) => start + i,
                    );
                    return (
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          minWidth: 62,
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {pagesToShow.map((pageNum) => (
                          <Box
                            key={`mobile-dot-${pageNum}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onPageChange(pageNum);
                            }}
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor:
                                pageNum === page ? "#6b7280" : "#e5e7eb",
                              cursor: "pointer",
                            }}
                          />
                        ))}
                      </Stack>
                    );
                  })()}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onNextPage();
                    }}
                    disabled={page === totalPages}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "6px",
                      backgroundColor: "#f5f5f5",
                      padding: "6px",
                      "&:hover": { backgroundColor: "#e5e5e5" },
                      "&:disabled": {
                        backgroundColor: "#f5f5f5",
                        opacity: 0.5,
                      },
                    }}
                  >
                    <ChevronRightIcon
                      sx={{
                        fontSize: 14,
                        color: "rgba(0,0,0,0.88)",
                      }}
                    />
                  </IconButton>
                </Stack>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Collapse>
    </Box>
  );
};

export default RepairsTable;
